import React, { useState, useRef } from 'react';
import {
  Plus, Edit3, Trash2, ChevronUp, ChevronDown, Copy, Upload, X, Check, Lock,
  Image as ImageIcon,
} from 'lucide-react';
import surveyApi from '../surveyApi';
import {
  QUESTION_TYPES, typeMeta, blankQuestion, defaultConfig, defaultOptions,
  hasOptions, isAnswerable, blankAnswer, LIKERT_PRESETS,
} from '../questionTypes';
import QuestionImage from '../components/QuestionImage';
import SurveyQuestionInput from '../components/SurveyQuestionInput';

const AI_PROMPT = `You are helping build an employee survey. Output ONLY a JSON array of question objects (no prose). Each object:
{
  "question_text": "string (required)",
  "question_type": "single_choice | multi_choice | dropdown | yes_no | rating | likert | nps | ranking | short_text | long_text | section",
  "help_text": "string (optional)",
  "is_required": true,
  "options": [ { "option_text": "string" } ],   // ONLY for single_choice, multi_choice, dropdown, yes_no, ranking (>=2)
  "config": { }                                   // optional, type-specific
}
Config by type:
  rating -> { "scale_min":1, "scale_max":5, "icon":"star", "min_label":"", "max_label":"" }
  likert -> { "points":[ {"value":1,"label":"Strongly disagree"}, ... ] }
  multi_choice -> { "min_select":0, "max_select":null }
  ranking -> { "require_all":true }   // options are the items to rank 1..N
  text types -> { "placeholder":"", "max_length":255 }
Surveys have NO correct answer. Produce ~8 well-rounded questions for the topic I describe next.`;

export default function SurveyQuestionEditor({ surveyId, questions, responseCount = 0, onChange }) {
  const [editing, setEditing] = useState(null); // question object being added/edited
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [newType, setNewType] = useState('single_choice');
  const [importOpen, setImportOpen] = useState(false);
  const [busyOrder, setBusyOrder] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const [uploadingItem, setUploadingItem] = useState(null); // ranking item index being uploaded
  const imgTimer = useRef(null);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 1800); };

  // ---- question image ----
  // For a SAVED question we persist immediately (works even when the question is
  // locked). For a NEW (unsaved) question we cache the File locally + show a local
  // preview, then commit it during save() once the question has an id.
  const handleImageFile = async (file) => {
    if (!editing || !file) return;
    setImageError('');
    if (!editing.id) {
      // Cache locally; applied on Save.
      if (editing._imagePreviewUrl) URL.revokeObjectURL(editing._imagePreviewUrl);
      const previewUrl = URL.createObjectURL(file);
      setEditing((e) => ({
        ...e,
        _imageFile: file,
        _imagePreviewUrl: previewUrl,
        config: {
          ...(e.config || {}),
          image: {
            width_pct: (e.config && e.config.image && e.config.image.width_pct) || 100,
            align: (e.config && e.config.image && e.config.image.align) || 'center',
            alt: (e.config && e.config.image && e.config.image.alt) || '',
          },
        },
      }));
      return;
    }
    setUploadingImage(true);
    try {
      const { image } = await surveyApi.adminUploadQuestionImage(surveyId, editing.id, file);
      setEditing((e) => ({ ...e, config: { ...(e.config || {}), image } }));
      onChange();
    } catch (e) {
      setImageError(e.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };
  const persistImageSettings = (image) => {
    if (imgTimer.current) clearTimeout(imgTimer.current);
    const qid = editing && editing.id;
    if (!qid || !image.filename) return; // unsaved/pending images are committed on Save
    imgTimer.current = setTimeout(() => {
      surveyApi.adminUpdateQuestionImage(surveyId, qid, {
        width_pct: image.width_pct, align: image.align, alt: image.alt,
      }).catch(() => {});
    }, 400);
  };
  const handleImageSetting = (patch) => {
    const image = { ...((editing.config && editing.config.image) || {}), ...patch };
    setEditing((e) => ({ ...e, config: { ...(e.config || {}), image } }));
    persistImageSettings(image);
  };
  const clearLocalImage = (e) => {
    if (e._imagePreviewUrl) URL.revokeObjectURL(e._imagePreviewUrl);
    const c = { ...(e.config || {}) }; delete c.image;
    return { ...e, _imageFile: null, _imagePreviewUrl: null, config: c };
  };
  const handleImageRemove = async () => {
    if (!editing) return;
    setImageError('');
    if (editing.id && editing.config && editing.config.image && editing.config.image.filename) {
      try {
        await surveyApi.adminDeleteQuestionImage(surveyId, editing.id);
        onChange();
      } catch (e) {
        setImageError(e.message || 'Remove failed');
        return;
      }
    }
    setEditing((e) => clearLocalImage(e));
  };

  // ---- ranking item images ----
  // Stored in config.item_images keyed by the item's option_value. Uploaded
  // immediately (needs a saved question id); the option's value is set here too so
  // it persists on Save and stays in sync with the image key.
  const handleItemImageFile = async (index, file) => {
    if (!editing || !file) return;
    setImageError('');
    if (!editing.id) { setImageError('Save the question first, then add item images.'); return; }
    const opt = (editing.options || [])[index];
    if (!opt) return;
    const optionValue = (opt.option_value && String(opt.option_value).trim()) || String(index + 1);
    setUploadingItem(index);
    try {
      if (!opt.option_value) setOption(index, { option_value: optionValue });
      const { item_images } = await surveyApi.adminUploadRankingItemImage(surveyId, editing.id, optionValue, file);
      setEditing((e) => ({ ...e, config: { ...(e.config || {}), item_images } }));
      onChange();
    } catch (e) {
      setImageError(e.message || 'Item image upload failed');
    } finally {
      setUploadingItem(null);
    }
  };

  const startNew = () => setEditing(blankQuestion(newType));
  const startEdit = (q) => setEditing(JSON.parse(JSON.stringify(q)));
  const cancel = () => {
    if (editing && editing._imagePreviewUrl) URL.revokeObjectURL(editing._imagePreviewUrl);
    setEditing(null); setError(''); setImageError('');
  };

  const patchEditing = (patch) => setEditing((e) => ({ ...e, ...patch }));
  const patchConfig = (patch) => setEditing((e) => ({ ...e, config: { ...(e.config || {}), ...patch } }));

  // ---- options ----
  const setOption = (i, patch) =>
    setEditing((e) => ({ ...e, options: e.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)) }));
  const addOption = () => setEditing((e) => ({ ...e, options: [...e.options, { option_text: '', option_value: null }] }));
  const removeOption = (i) => setEditing((e) => ({ ...e, options: e.options.filter((_, idx) => idx !== i) }));

  // ---- likert points ----
  const setPoint = (i, patch) =>
    setEditing((e) => {
      const points = (e.config.points || []).map((p, idx) => (idx === i ? { ...p, ...patch } : p));
      return { ...e, config: { ...e.config, points } };
    });
  const addPoint = () =>
    setEditing((e) => {
      const points = e.config.points || [];
      const value = points.length ? Math.max(...points.map((p) => p.value)) + 1 : 1;
      return { ...e, config: { ...e.config, points: [...points, { value, label: '' }] } };
    });
  const removePoint = (i) =>
    setEditing((e) => ({ ...e, config: { ...e.config, points: (e.config.points || []).filter((_, idx) => idx !== i) } }));

  const changeType = (key) =>
    setEditing((e) => ({
      ...e,
      question_type: key,
      // Reset type-specific config, but keep any attached image (it's not type-specific).
      config: { ...defaultConfig(key), ...(e.config && e.config.image ? { image: e.config.image } : {}) },
      options: hasOptions(key) ? (e.options && e.options.length >= 2 && key !== 'yes_no' ? e.options : defaultOptions(key)) : [],
    }));

  const validate = (q) => {
    if (!q.question_text || !q.question_text.trim()) return 'Question text is required.';
    if (hasOptions(q.question_type)) {
      const opts = (q.options || []).filter((o) => o.option_text && o.option_text.trim());
      if (opts.length < 2) return 'This question needs at least 2 non-empty options.';
    }
    if (q.question_type === 'likert' && (!q.config.points || q.config.points.length < 2)) {
      return 'Likert questions need at least 2 scale points.';
    }
    return null;
  };

  const save = async () => {
    const err = validate(editing);
    if (err) { setError(err); return; }
    try {
      const payload = {
        question_id: editing.id || undefined,
        question_text: editing.question_text.trim(),
        help_text: editing.help_text || null,
        question_type: editing.question_type,
        is_required: !!editing.is_required,
        question_order: editing.id != null ? editing.question_order : questions.length,
        config: editing.config || {},
        options: hasOptions(editing.question_type)
          ? editing.options.filter((o) => o.option_text && o.option_text.trim())
              .map((o) => ({ option_text: o.option_text.trim(), option_value: o.option_value ?? null }))
          : undefined,
      };
      const res = await surveyApi.adminUpsertQuestion(surveyId, payload);
      const qId = res.id || editing.id;
      // Mark as persisted up-front so a retry (e.g. if the image upload fails)
      // updates this question instead of creating a duplicate.
      if (!editing.id && qId) setEditing((e) => ({ ...e, id: qId }));

      // Commit a cached (not-yet-uploaded) image now that the question has an id.
      if (editing._imageFile && qId) {
        await surveyApi.adminUploadQuestionImage(surveyId, qId, editing._imageFile);
        setEditing((e) => ({ ...e, _imageFile: null }));
      }
      if (editing._imagePreviewUrl) URL.revokeObjectURL(editing._imagePreviewUrl);

      setEditing(null);
      setError('');
      onChange();
    } catch (e) {
      setError(e.message || 'Failed to save question');
    }
  };

  const remove = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try { await surveyApi.adminDeleteQuestion(qid); onChange(); }
    catch (e) { setError(e.message || 'Failed to delete'); }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= questions.length) return;
    const order = questions.map((q) => q.id);
    [order[index], order[target]] = [order[target], order[index]];
    try {
      setBusyOrder(true);
      await surveyApi.adminReorderQuestions(surveyId, order);
      onChange();
    } catch (e) {
      setError(e.message || 'Failed to reorder');
    } finally {
      setBusyOrder(false);
    }
  };

  const copyAiPrompt = async () => {
    try { await navigator.clipboard.writeText(AI_PROMPT); flash('AI prompt copied'); }
    catch (_) { setError('Could not copy to clipboard'); }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="text-lg font-semibold text-slate-100">
          Questions ({questions.filter((q) => q.question_type !== 'section').length})
        </h3>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-emerald-300">{msg}</span>}
          <button onClick={copyAiPrompt}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm flex items-center gap-1">
            <Copy className="w-4 h-4" /> Copy AI Prompt
          </button>
          <button onClick={() => setImportOpen(true)}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm flex items-center gap-1">
            <Upload className="w-4 h-4" /> Import
          </button>
          <select value={newType} onChange={(e) => setNewType(e.target.value)}
            className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 text-sm">
            {QUESTION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button onClick={startNew}
            className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {error && <div className="mb-3 p-2 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm">{error}</div>}

      {/* Question list */}
      <ul className="space-y-2">
        {questions.length === 0 && <li className="text-sm text-slate-400">No questions yet. Pick a type and click Add.</li>}
        {questions.map((q, i) => {
          const meta = typeMeta(q.question_type);
          const Icon = meta.icon;
          return (
            <li key={q.id} className="p-3 rounded bg-slate-900 border border-slate-700 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex flex-col">
                  <button disabled={busyOrder || i === 0} onClick={() => move(i, -1)}
                    className="text-slate-500 hover:text-slate-200 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button disabled={busyOrder || i === questions.length - 1} onClick={() => move(i, 1)}
                    className="text-slate-500 hover:text-slate-200 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-300 shrink-0">
                  <Icon className="w-3.5 h-3.5" /> {meta.short}
                </span>
                <span className="text-slate-100 truncate">
                  {q.question_text || <span className="text-slate-500 italic">(untitled)</span>}
                  {q.is_required && <span className="text-rose-400 ml-1">*</span>}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {q.answer_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-sky-300" title={`${q.answer_count} responses — locked`}>
                    <Lock className="w-3.5 h-3.5" /> {q.answer_count}
                  </span>
                )}
                {q.config && q.config.image && q.config.image.filename && (
                  <ImageIcon className="w-3.5 h-3.5 text-slate-400" aria-label="Has image" />
                )}
                {hasOptions(q.question_type) && <span className="text-xs text-slate-500">{q.options.length} opts</span>}
                <button onClick={() => startEdit(q)} className="text-amber-400 hover:text-amber-300" title="Edit"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => remove(q.id)} disabled={q.answer_count > 0}
                  title={q.answer_count > 0 ? 'Has responses — cannot delete' : 'Delete'}
                  className={q.answer_count > 0 ? 'text-slate-600 cursor-not-allowed' : 'text-rose-400 hover:text-rose-300'}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Inline editor */}
      {editing && (
        <div className="mt-4 rounded-lg bg-slate-900 border border-amber-700 p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-slate-400">Type</label>
            <select value={editing.question_type} onChange={(e) => changeType(e.target.value)}
              disabled={editing.answer_count > 0}
              className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm disabled:opacity-50">
              {QUESTION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <span className="text-xs text-slate-500">{typeMeta(editing.question_type).desc}</span>
          </div>
          {editing.answer_count > 0 && (
            <div className="text-xs text-sky-300 flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" /> {editing.answer_count} response{editing.answer_count === 1 ? '' : 's'} — type &amp; options are locked; you can still edit the wording.
            </div>
          )}

          <textarea rows={2} value={editing.question_text} placeholder={editing.question_type === 'section' ? 'Section heading' : 'Question text'}
            onChange={(e) => patchEditing({ question_text: e.target.value })}
            className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100" />

          <input value={editing.help_text || ''} placeholder="Help text / description (optional)"
            onChange={(e) => patchEditing({ help_text: e.target.value })}
            className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />

          {/* Image (works for every type, including section headings) */}
          <div className="rounded border border-slate-700 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-slate-400 flex items-center gap-1">
                <ImageIcon className="w-3.5 h-3.5" /> Image
              </span>
              {editing.config && editing.config.image && (editing.config.image.filename || editing._imageFile) ? (
                <button type="button" onClick={handleImageRemove}
                  className="text-rose-400 hover:text-rose-300 text-xs flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              ) : null}
            </div>
            {!(editing.config && editing.config.image && (editing.config.image.filename || editing._imageFile)) ? (
              <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm cursor-pointer w-fit">
                <Upload className="w-4 h-4" /> {uploadingImage ? 'Uploading…' : 'Upload image'}
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden"
                  onChange={(e) => { if (e.target.files[0]) handleImageFile(e.target.files[0]); e.target.value = ''; }} />
              </label>
            ) : (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                    <span>Width</span><span>{editing.config.image.width_pct || 100}%</span>
                  </div>
                  <input type="range" min={5} max={100} value={editing.config.image.width_pct || 100}
                    onChange={(e) => handleImageSetting({ width_pct: Number(e.target.value) })}
                    className="w-full accent-amber-500" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Align</span>
                  {['left', 'center', 'right'].map((a) => (
                    <button key={a} type="button" onClick={() => handleImageSetting({ align: a })}
                      className={`px-2 py-1 rounded text-xs capitalize ${
                        (editing.config.image.align || 'center') === a ? 'bg-amber-600 text-white' : 'bg-slate-700 text-slate-200 hover:bg-slate-600'}`}>
                      {a}
                    </button>
                  ))}
                </div>
                <input value={editing.config.image.alt || ''} placeholder="Alt text (for accessibility)"
                  onChange={(e) => handleImageSetting({ alt: e.target.value })}
                  className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
                <label className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-xs cursor-pointer w-fit">
                  <Upload className="w-3.5 h-3.5" /> {uploadingImage ? 'Uploading…' : 'Replace image'}
                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden"
                    onChange={(e) => { if (e.target.files[0]) handleImageFile(e.target.files[0]); e.target.value = ''; }} />
                </label>
              </div>
            )}
            {imageError && <p className="text-xs text-rose-400">{imageError}</p>}
          </div>

          {/* Type-specific config — hidden once the question has responses (locked) */}
          {editing.answer_count > 0 ? null : (
            <TypeConfigEditor
              editing={editing} patchConfig={patchConfig}
              setOption={setOption} addOption={addOption} removeOption={removeOption}
              setPoint={setPoint} addPoint={addPoint} removePoint={removePoint}
              surveyId={surveyId} onItemImageFile={handleItemImageFile} uploadingItem={uploadingItem}
            />
          )}

          {editing.question_type !== 'section' && (
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="accent-amber-500" checked={!!editing.is_required}
                onChange={(e) => patchEditing({ is_required: e.target.checked })} />
              <span className="text-sm text-slate-200">Required</span>
            </label>
          )}

          {/* Live preview — exactly how respondents will see this question */}
          <div className="pt-1">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Live preview</div>
            <QuestionPreview
              key={`${editing.id || 'new'}-${editing.question_type}`}
              surveyId={surveyId}
              localImageSrc={editing._imagePreviewUrl}
              question={{
                id: editing.id || 'preview',
                question_text: editing.question_text,
                help_text: editing.help_text,
                question_type: editing.question_type,
                is_required: editing.is_required,
                config: editing.config || {},
                options: (editing.options || []).map((o, i) => ({
                  id: o.id != null ? o.id : `tmp-${i}`,
                  option_text: o.option_text || `Option ${i + 1}`,
                  option_value: o.option_value,
                  option_order: i,
                })),
              }}
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={cancel} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm">Cancel</button>
            <button onClick={save} className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm">
              {editing.id ? 'Save Changes' : 'Add Question'}
            </button>
          </div>
        </div>
      )}

      {importOpen && (
        <ImportModal surveyId={surveyId} responseCount={responseCount} onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); onChange(); }} />
      )}
    </div>
  );
}

// ---------------- live preview of a single question ----------------
function QuestionPreview({ surveyId, question, localImageSrc }) {
  const [answer, setAnswer] = useState(blankAnswer());
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
      <div className="mb-3">
        <span className="text-slate-100 font-medium">
          {question.question_text || <span className="text-slate-500 italic">(question text)</span>}
          {question.is_required && <span className="text-rose-400 ml-1">*</span>}
        </span>
        {question.help_text && <p className="text-sm text-slate-400 mt-1 whitespace-pre-wrap">{question.help_text}</p>}
      </div>
      <QuestionImage surveyId={surveyId} question={question} localSrc={localImageSrc} />
      {isAnswerable(question.question_type) && (
        <SurveyQuestionInput surveyId={surveyId} question={question} answer={answer} onChange={setAnswer} />
      )}
    </div>
  );
}

// ---------------- type-specific config editors ----------------
function TypeConfigEditor({ editing, patchConfig, setOption, addOption, removeOption, setPoint, addPoint, removePoint, surveyId, onItemImageFile, uploadingItem }) {
  const type = editing.question_type;
  const cfg = editing.config || {};

  if (type === 'yes_no') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {editing.options.map((o, i) => (
          <input key={i} value={o.option_text} onChange={(e) => setOption(i, { option_text: e.target.value })}
            className="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
        ))}
      </div>
    );
  }

  if (hasOptions(type)) {
    const optionNoun = type === 'ranking' ? 'Item' : 'Option';
    const itemImages = cfg.item_images || {};
    return (
      <div className="space-y-2">
        {editing.options.map((o, i) => {
          const optionValue = (o.option_value && String(o.option_value).trim()) || String(i + 1);
          const thumb = itemImages[optionValue];
          return (
            <div key={i} className="flex items-center gap-2">
              {type === 'ranking' && (
                <ItemImageButton
                  thumbUrl={thumb && surveyId ? surveyApi.surveyImageUrl(surveyId, thumb) : null}
                  uploading={uploadingItem === i}
                  onFile={(f) => onItemImageFile(i, f)} />
              )}
              <input value={o.option_text} placeholder={`${optionNoun} ${i + 1}`}
                onChange={(e) => setOption(i, { option_text: e.target.value })}
                className="flex-1 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
              {editing.options.length > 2 && (
                <button onClick={() => removeOption(i)} className="text-rose-400 hover:text-rose-300"><X className="w-4 h-4" /></button>
              )}
            </div>
          );
        })}
        <button onClick={addOption} className="text-amber-400 hover:text-amber-300 text-sm">+ Add another {optionNoun.toLowerCase()}</button>
        {type === 'ranking' && (
          <p className="text-xs text-slate-400">
            Optional: give each item an image (the icon on the left) — respondents drag the items into
            order.{!editing.id && ' Save the question first to attach item images.'}
          </p>
        )}
        {type === 'multi_choice' && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <NumberField label="Min selections" value={cfg.min_select ?? 0} onChange={(v) => patchConfig({ min_select: v })} />
            <NumberField label="Max selections (blank = any)" value={cfg.max_select ?? ''} onChange={(v) => patchConfig({ max_select: v === '' ? null : v })} />
          </div>
        )}
        {type === 'ranking' && (
          <label className="inline-flex items-center gap-2 cursor-pointer pt-1">
            <input type="checkbox" className="accent-amber-500" checked={cfg.require_all !== false}
              onChange={(e) => patchConfig({ require_all: e.target.checked })} />
            <span className="text-sm text-slate-200">Require every item to be ranked</span>
          </label>
        )}
      </div>
    );
  }

  if (type === 'rating') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Min" value={cfg.scale_min ?? 1} onChange={(v) => patchConfig({ scale_min: v })} />
        <NumberField label="Max" value={cfg.scale_max ?? 5} onChange={(v) => patchConfig({ scale_max: v })} />
        <div>
          <label className="block text-xs text-slate-400 mb-1">Display</label>
          <select value={cfg.icon || 'star'} onChange={(e) => patchConfig({ icon: e.target.value })}
            className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm">
            <option value="star">Stars</option>
            <option value="number">Numbers</option>
          </select>
        </div>
        <div />
        <TextField label="Low-end label" value={cfg.min_label || ''} onChange={(v) => patchConfig({ min_label: v })} />
        <TextField label="High-end label" value={cfg.max_label || ''} onChange={(v) => patchConfig({ max_label: v })} />
      </div>
    );
  }

  if (type === 'likert') {
    const points = cfg.points || [];
    return (
      <div className="space-y-2">
        <div className="flex gap-2 text-xs">
          <span className="text-slate-400">Presets:</span>
          <button onClick={() => patchConfig({ points: LIKERT_PRESETS.agreement })} className="text-amber-400 hover:text-amber-300">Agreement</button>
          <button onClick={() => patchConfig({ points: LIKERT_PRESETS.satisfaction })} className="text-amber-400 hover:text-amber-300">Satisfaction</button>
        </div>
        {points.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="number" value={p.value} onChange={(e) => setPoint(i, { value: Number(e.target.value) })}
              className="w-16 px-2 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
            <input value={p.label} placeholder="Label" onChange={(e) => setPoint(i, { label: e.target.value })}
              className="flex-1 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
            {points.length > 2 && (
              <button onClick={() => removePoint(i)} className="text-rose-400 hover:text-rose-300"><X className="w-4 h-4" /></button>
            )}
          </div>
        ))}
        <button onClick={addPoint} className="text-amber-400 hover:text-amber-300 text-sm">+ Add scale point</button>
      </div>
    );
  }

  if (type === 'nps') {
    return <p className="text-xs text-slate-400">Net Promoter Score uses a fixed 0–10 scale.</p>;
  }

  if (type === 'short_text' || type === 'long_text') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <TextField label="Placeholder" value={cfg.placeholder || ''} onChange={(v) => patchConfig({ placeholder: v })} />
        <NumberField label="Max length" value={cfg.max_length ?? ''} onChange={(v) => patchConfig({ max_length: v === '' ? null : v })} />
      </div>
    );
  }

  return null;
}

// Small square button to attach/replace a ranking item's image; shows a thumbnail once set.
function ItemImageButton({ thumbUrl, uploading, onFile }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden"
        onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) onFile(f); e.target.value = ''; }} />
      <button type="button" onClick={() => ref.current && ref.current.click()} disabled={uploading}
        title={thumbUrl ? 'Replace item image' : 'Add item image'}
        className="flex-shrink-0 w-10 h-10 rounded border border-slate-700 bg-slate-800 hover:bg-slate-700 flex items-center justify-center overflow-hidden disabled:opacity-60">
        {uploading
          ? <span className="text-[10px] text-slate-400">…</span>
          : thumbUrl
            ? <img src={thumbUrl} alt="" className="w-full h-full object-contain" />
            : <ImageIcon className="w-4 h-4 text-slate-400" />}
      </button>
    </>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input type="number" value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
    </div>
  );
}
function TextField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm" />
    </div>
  );
}

// ---------------- import modal ----------------
function ImportModal({ surveyId, responseCount = 0, onClose, onDone }) {
  const [text, setText] = useState('');
  const [replace, setReplace] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const doImport = async () => {
    let parsed;
    try { parsed = JSON.parse(text); }
    catch (e) { setError('Invalid JSON.'); return; }
    if (!Array.isArray(parsed) || parsed.length === 0) { setError('Expected a non-empty JSON array.'); return; }
    try {
      setBusy(true);
      const res = await surveyApi.adminImportQuestions(surveyId, parsed, replace);
      setBusy(false);
      onDone(res);
    } catch (e) {
      setBusy(false);
      setError(e.message || 'Import failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-slate-800 border border-slate-700 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold text-slate-100">Import questions (JSON)</h4>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-400 mb-2">
          Paste a JSON array of questions (use the “Copy AI Prompt” button to generate one with an AI assistant).
        </p>
        <textarea rows={10} value={text} onChange={(e) => setText(e.target.value)}
          placeholder='[ { "question_text": "...", "question_type": "single_choice", "options": [ {"option_text":"A"}, {"option_text":"B"} ] } ]'
          className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100 font-mono text-xs" />
        {error && <div className="mt-2 p-2 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm">{error}</div>}
        <div className="mt-3 flex items-center justify-between">
          <label className={`inline-flex items-center gap-2 text-sm ${responseCount > 0 ? 'text-slate-500 cursor-not-allowed' : 'text-slate-200 cursor-pointer'}`}
            title={responseCount > 0 ? 'Disabled: this survey has responses' : ''}>
            <input type="checkbox" className="accent-amber-500" checked={replace && responseCount === 0}
              disabled={responseCount > 0} onChange={(e) => setReplace(e.target.checked)} />
            Replace all existing questions{responseCount > 0 ? ' (disabled — survey has responses)' : ''}
          </label>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm">Cancel</button>
            <button onClick={doImport} disabled={busy}
              className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm flex items-center gap-1 disabled:opacity-60">
              <Check className="w-4 h-4" /> {busy ? 'Importing…' : 'Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
